// Hooks rund um den SICHTBAREN Viewport (window.visualViewport).
//
// Der Layout-Viewport (100vh / innerHeight) bleibt bei geöffneter
// Bildschirmtastatur unverändert — der Visual-Viewport schrumpft. Bottom-
// Sheets müssen sich am Visual-Viewport dimensionieren, sonst liegen
// Footer/Felder hinter der Tastatur. iOS-Hinweis: `interactive-widget`
// wirkt dort nicht, visualViewport.height dagegen schon.
import { useEffect, useRef, useState } from 'react'
import { computeSheetMaxHeight } from './sheet-height'

function readVisualHeight(): number {
  if (typeof window === 'undefined') return 0
  return window.visualViewport?.height ?? window.innerHeight
}

/**
 * Sichtbare Viewport-Höhe in px, live über visualViewport `resize` +
 * `scroll` (per rAF gethrottlet — die Events feuern bei Scroll-Bounce in
 * Bursts). Fallback ohne visualViewport-Support: window.innerHeight.
 */
export function useVisualViewportHeight(): number {
  const [height, setHeight] = useState(readVisualHeight)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) {
      const onResize = () => setHeight(window.innerHeight)
      window.addEventListener('resize', onResize)
      onResize()
      return () => window.removeEventListener('resize', onResize)
    }
    let frame = 0
    const onChange = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        setHeight(vv.height)
      })
    }
    vv.addEventListener('resize', onChange)
    vv.addEventListener('scroll', onChange)
    onChange()
    return () => {
      if (frame) cancelAnimationFrame(frame)
      vv.removeEventListener('resize', onChange)
      vv.removeEventListener('scroll', onChange)
    }
  }, [])

  return height
}

export interface VisualViewportRect {
  /** Sichtbare Viewport-Höhe (px). */
  height: number
  /** Versatz der Viewport-Oberkante gegenüber dem Layout-Viewport (px) —
   *  auf iOS beim Scroll-in-Feld > 0. */
  offsetTop: number
}

function readVisualRect(): VisualViewportRect {
  if (typeof window === 'undefined') return { height: 0, offsetTop: 0 }
  const vv = window.visualViewport
  return { height: vv?.height ?? window.innerHeight, offsetTop: vv?.offsetTop ?? 0 }
}

/**
 * Sichtbares Viewport-Rechteck (Höhe + Top-Offset), live über
 * visualViewport `resize`+`scroll` (rAF-gethrottlet). Für Mobile-Bottom-
 * Sheets, die ÜBER der Bildschirmtastatur bleiben müssen: den Portal-
 * Wrapper auf `top: offsetTop; height` setzen — dann sitzt ein darin
 * `bottom-0`-verankertes Sheet am unteren Rand des SICHTBAREN Bereichs
 * statt hinter der Tastatur (der Layout-Viewport schrumpft auf iOS NICHT).
 */
export function useVisualViewportRect(): VisualViewportRect {
  const [rect, setRect] = useState(readVisualRect)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) {
      const onResize = () => setRect({ height: window.innerHeight, offsetTop: 0 })
      window.addEventListener('resize', onResize)
      onResize()
      return () => window.removeEventListener('resize', onResize)
    }
    let frame = 0
    const onChange = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        setRect({ height: vv.height, offsetTop: vv.offsetTop })
      })
    }
    vv.addEventListener('resize', onChange)
    vv.addEventListener('scroll', onChange)
    onChange()
    return () => {
      if (frame) cancelAnimationFrame(frame)
      vv.removeEventListener('resize', onChange)
      vv.removeEventListener('scroll', onChange)
    }
  }, [])

  return rect
}

/**
 * Fertige maxHeight (px) für ein Mobile-Bottom-Sheet:
 * `min(90% Layout-Viewport, Visual-Viewport − Offset)`.
 */
export function useSheetMaxHeight(): number {
  const visualHeight = useVisualViewportHeight()
  // Layout-Höhe ABONNIEREN statt beim Render einfrieren — beim iOS-
  // Standalone-Kaltstart ist window.innerHeight anfangs stale und die
  // 90%-Kappe bliebe sonst bis zur ersten Geste falsch (PWA-Start-Bug).
  const [layoutHeight, setLayoutHeight] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight,
  )
  useEffect(() => {
    const onResize = () => setLayoutHeight(window.innerHeight)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    window.addEventListener('pageshow', onResize)
    onResize()
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
      window.removeEventListener('pageshow', onResize)
    }
  }, [])
  return computeSheetMaxHeight(visualHeight, layoutHeight)
}

/** Verzögerung, bis die Tastatur-Einblend-Animation durch ist. */
const FOCUS_SCROLL_DELAY_MS = 250

/**
 * `focusin`-Handler für scrollbare Sheet-Bodies: hält das fokussierte
 * Input/Textarea/Select nach dem Tastatur-Einblenden sichtbar
 * (`scrollIntoView({ block: 'nearest' })` nach kurzem Delay).
 * Ref auf den Scroll-Container hängen; `enabled` nur am Phone setzen.
 */
export function useFocusScrollIntoView<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T | null>(null)

  useEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el) return
    let timer = 0
    const onFocusIn = (e: Event) => {
      const target = e.target
      const isField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      if (!isField) return
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        // Element kann inzwischen unmounted/blurred sein — dann no-op.
        if (document.activeElement === target) {
          target.scrollIntoView({ block: 'nearest' })
        }
      }, FOCUS_SCROLL_DELAY_MS)
    }
    el.addEventListener('focusin', onFocusIn)
    return () => {
      window.clearTimeout(timer)
      el.removeEventListener('focusin', onFocusIn)
    }
  }, [enabled])

  return ref
}
